import { Dispatch, useState } from 'react';
import { set } from 'lodash';
import { DeepPartial, Flatten, FormRisk, FormTruckCompositionOwner } from '@/types';
import { useAppContext } from '@/context';
import { useToast } from '@/hooks';
import { deepFind, deepMerge, useSetFormValue } from '@/utils';
import { loadRNTRCDataWithUUIDService } from '@/services';
import { EnabledFields } from '../../Steps/Compositions/types';

const checkIsValidRntrcLen = (rntrc: string) => {
  if (!rntrc) return false;
  return [8, 9].includes(rntrc.length);
};

export const useRNTRCSearch = (form: FormRisk, setterCallback: Dispatch<React.SetStateAction<EnabledFields>>) => {
  const [loadingRNTRC, setLoadingRNTRC] = useState<boolean[]>([]);
  const [loadedRNTRC, setLoadedRNTRC] = useState<DeepPartial<FormTruckCompositionOwner>[]>([]);
  const toast = useToast();
  const { dispatch, state } = useAppContext();
  const setFormValue = useSetFormValue(dispatch);

  const searchRNTRC = (rntrc: string, position: number) => {
    const isValidRntrcLen = checkIsValidRntrcLen(rntrc);

    if (!isValidRntrcLen) {
      setFormValue(`truck.compositions.${position}.owner.rntrcUuid`, '');
      return;
    }

    if (state.risk.data.truck.compositions[position].owner.rntrcUuid) return;

    const getEmptyRNTRC = (): DeepPartial<FormTruckCompositionOwner> => ({
      address: {
        complement: '',
        location: undefined,
        neighborhood: '',
        number: '',
        street: '',
        zipCode: '',
      },
      cellPhone: '',
      cpfCnpj: '',
      name: '',
      rntrc,
      rntrcUuid: '',
      rntrcCpfCnpj: '',
      rntrcName: '',
      rntrcOwnerIsTruckOwner: false,
    });

    const { compositions } = form.truck;
    setLoadingRNTRC(() => Array.from(Array(compositions.length), (_, index) => index === position));

    loadRNTRCDataWithUUIDService(rntrc.padStart(9, '0'))
      .then((data) => {
        setLoadedRNTRC((old) =>
          Array.from(Array(compositions.length), (_, index) =>
            index === position ? data : old[index] ?? getEmptyRNTRC(),
          ),
        );

        if (data?.document && data?.name) {
          toast.add({ type: 'success', title: 'Dados encontrados com sucesso!' });
          setterCallback((previousState) => {
            const newState = structuredClone(previousState);

            set(newState, `truck.compositions.${position}.owner.rntrcCpfCnpj`, false);
            set(newState, `truck.compositions.${position}.owner.rntrcName`, false);

            return newState;
          });
        } else {
          setterCallback((previousState) => {
            const newState = structuredClone(previousState);

            set(newState, `truck.compositions.${position}.owner.rntrcCpfCnpj`, true);
            set(newState, `truck.compositions.${position}.owner.rntrcName`, true);

            return newState;
          });
        }

        const finalData = deepMerge(form.truck.compositions[position].owner, {
          rntrcCpfCnpj: data?.document,
          rntrcName: data?.name,
          rntrcUuid: data?.rntrcUuid,
        });
        setFormValue(`truck.compositions.${position}.owner`, finalData);
      })
      .catch(() => {
        setterCallback((previousState) => {
          const newState = structuredClone(previousState);

          set(newState, `truck.compositions.${position}.owner.rntrcCpfCnpj`, true);
          set(newState, `truck.compositions.${position}.owner.rntrcName`, true);

          return newState;
        });

        setLoadedRNTRC([]);
        const emptyRNTRC = getEmptyRNTRC();
        const finalData = deepMerge(form.truck.compositions[position].owner, {
          rntrcCpfCnpj: emptyRNTRC?.rntrcCpfCnpj,
          rntrcName: emptyRNTRC?.rntrcName,
          rntrcUuid: emptyRNTRC?.rntrcUuid,
        });
        setFormValue(`truck.compositions.${position}.owner`, finalData);
      })
      .finally(() => {
        setLoadingRNTRC((old) => old.map((item, index) => (index === position ? false : item)));
      });
  };

  const isLoadedRNTRCField = (position: number, path: keyof Flatten<FormTruckCompositionOwner>): boolean => {
    if (!Boolean(loadedRNTRC[position])) return false;
    const dataRNTRC = loadedRNTRC[position];
    return dataRNTRC.rntrc && Boolean(deepFind(dataRNTRC, path));
  };

  return { searchRNTRC, loadingRNTRC, isLoadedRNTRCField };
};
